import { useId } from "react";
import PropTypes from "prop-types";
import { Box, styled, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";

import FieldLabel from "./FieldLabel.jsx";

/**
 * The standard text field for dialog forms.
 *
 * **The label sits above the input, not inside it.** MUI's outlined variant
 * floats the label into a gap it cuts in the border, and it sizes that gap from
 * a hidden `<legend>` that does not inherit the label's own styling. Our labels
 * are uppercase, bold and letter-spaced, so they render wider than the gap
 * reserved for them — and the longest one, "ADDITIONAL MESSAGE (OPTIONAL)",
 * visibly overlapped the border. Widening the notch would only move the
 * threshold; taking the label out of the border removes the failure mode, and
 * every call site was already passing `InputLabelProps={{ shrink: true }}` to
 * keep the label pinned up there anyway.
 *
 * A real `<label htmlFor>` also names the input natively, which is the pattern
 * the rest of the app uses (see docs/accessibility.md).
 *
 * Helper text still goes through MUI so it keeps its `aria-describedby` wiring.
 */

/* Fields default to `small`. Half the call sites passed it and half did not,
   which is most of why these forms read as uneven: a multiline field at the
   default size is 8px taller per row than its neighbours for no reason. */
const DEFAULT_SIZE = "small";

/* Placeholders are real text to a reader who needs contrast. MUI's default is
   the text colour at 42% opacity, which lands around 2.4:1. This is 4.8:1. */
const PLACEHOLDER_COLOR = "#6b7280";

const StyledTextField = styled(TextField)(({ theme }) => {
  const surface = theme.palette.common.white;
  return {
    "& .MuiOutlinedInput-root": {
      backgroundColor: surface,
      borderRadius: 10,
      fontSize: "0.9rem",
      color: theme.palette.text.primary,
      transition: "border-color .15s ease, box-shadow .15s ease",
      "& fieldset": {
        borderColor: theme.palette.grey[300],
      },
      "&:hover fieldset": {
        borderColor: theme.palette.grey[400],
      },
      "&.Mui-focused": {
        // A ring rather than a thicker border: a border that changes width
        // reflows the text inside it by half a pixel on every focus.
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
      "&.Mui-error fieldset": {
        borderColor: theme.palette.error.main,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.16)}`,
      },
    },

    /* One padding value for both shapes, so a textarea lines up with the inputs
       above it instead of sitting on its own inset. */
    "& .MuiOutlinedInput-input": {
      padding: "10px 12px",
    },
    "& .MuiInputBase-multiline": {
      padding: "10px 12px",
    },
    "& .MuiInputBase-multiline .MuiOutlinedInput-input": {
      padding: 0,
    },

    "& .MuiOutlinedInput-input::placeholder": {
      color: PLACEHOLDER_COLOR,
      opacity: 1,
    },

    /* Browser autofill repaints the field pale blue and ignores background-color,
       which is why the first field in a saved form looked like a different
       control from the ones under it. The inset shadow is the one override
       autofill respects. */
    "& .MuiOutlinedInput-input:-webkit-autofill, & .MuiOutlinedInput-input:autofill": {
      WebkitBoxShadow: `0 0 0 100px ${surface} inset`,
      boxShadow: `0 0 0 100px ${surface} inset`,
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
      borderRadius: "inherit",
    },

    "& .MuiFormHelperText-root.Mui-error": {
      color: theme.palette.error.text,
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.75rem",
      lineHeight: 1.4,
      marginLeft: 0,
      marginRight: 0,
      marginTop: theme.spacing(0.75),
    },
  };
});

/* A trailing "(optional)" in the label becomes the quiet tag FieldLabel renders,
   rather than staying part of the field's name. It reads as metadata instead of
   as something the field is called — and it means the longest labels are no
   longer the ones most likely to look wrong. Handled here so the existing call
   sites keep passing the strings they already pass. */
const OPTIONAL_SUFFIX = /\s*\(\s*optional\s*\)\s*$/i;

function DialogTextField({
  label,
  required = false,
  id,
  size = DEFAULT_SIZE,
  fullWidth = true,
  sx,
  ...rest
}) {
  // Stable per instance, so the label's htmlFor and the input's id agree across
  // renders without every caller having to invent an id.
  const generatedId = useId();
  const fieldId = id || `dialog-field-${generatedId}`;

  const hasStringLabel = typeof label === "string";
  const isOptional = hasStringLabel && OPTIONAL_SUFFIX.test(label);
  const labelText = isOptional ? label.replace(OPTIONAL_SUFFIX, "") : label;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        width: fullWidth ? "100%" : undefined,
        ...sx,
      }}
    >
      {label ? (
        <FieldLabel htmlFor={fieldId} required={required} optional={isOptional}>
          {labelText}
        </FieldLabel>
      ) : null}

      <StyledTextField
        {...rest}
        id={fieldId}
        required={required}
        size={size}
        fullWidth={fullWidth}
      />
    </Box>
  );
}

DialogTextField.propTypes = {
  /** Rendered above the field. A trailing "(optional)" becomes a quiet tag. */
  label: PropTypes.node,
  required: PropTypes.bool,
  id: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};

export default DialogTextField;
